import React, { Component } from 'react';
import { connect } from 'react-redux';
import { matchRoutes, renderRoutes, withRouter } from 'mage-react-router';
import { i18nMark } from '@lingui/react';
import { parseMobile } from '@moka-fe/ats.utils';
import { loadComponent } from '@moka-fe/mage-micro-app';
import { cloneDeep, isEmpty } from 'lodash';
import { Loading } from 'sugar-design';

import Breadcrumb from '@/components/common/Breadcrumb';
import JobHeader from '@/components/job/jobApply/JobHeader';

import {
  APPLICATION_ATTACHMENT_TYPE,
  CURRENT_COUNTRY_CALLING_CODE,
  HEADHUNTER_TYPE,
  HIRE_MODES,
  SET_ACTION,
} from '@/constants/common';
import { uploadCandidateForHeadhunter } from '@/redux/actions/applications';
import { getApplyFormExtendInfo } from '@/redux/actions/apply';
import { ERROR_CODES } from '@/tools/mokaRequest/errorCode';
import mokaRequest from '@/tools/mokaRequest';
import {
  candidateToApplyData,
  dealCustomDataForApplyForm,
  dealCustomDataForUpdateCandidate,
  delNotRequireField,
  handleEndDate,
  handleUploadInfoType,
  removeNonDisplayFields,
} from '@/utils/applyForm';
import { getI18n } from '@/utils/i18n';
import {
  loadLanguagesForApplyInfo,
  transformApplyFormLocale,
} from '@/utils/loadLanguagesForApplyInfo';

const HeadhunterWeb = React.lazy(() => loadComponent('apply-form.HeadhunterWeb'));

const i18n = getI18n();

const mapStateToProps = (state) => {
  const applicationEntities = state.applications?.entities ?? {};
  const overseasNumberSetting = state.org.overseasNumberSetting;
  Object.keys(applicationEntities).forEach((key) => {
    if (applicationEntities[key].fullPhone) {
      applicationEntities[key].phone = applicationEntities[key].fullPhone;
    }
  });
  const applyInfoCallingCodeList = overseasNumberSetting.countryCallingCodes?.length
    ? overseasNumberSetting.countryCallingCodes.map((item) => ({
        label: '+' + item.code,
        value: item.code,
      }))
    : null;
  return {
    orgEntities: state.org.entities,
    jobEntities: state.jobs.entities,
    applicationEntities,
    applyInfoCallingCodeList,
    applyForm: state.org.applyForm,
    setting: state.org.setting,
  };
};

const mapDispatchToProps = {
  uploadCandidateForHeadhunter,
};

@withRouter
@connect(mapStateToProps, mapDispatchToProps)
class UpdateCandidateInfo extends Component {
  constructor(props) {
    super(props);
    this.state = {
      initialValue: {
        uploadInfo: {
          // 上传
          resumeKey: '', // 上传简历
          resumeName: '', // 简历名称
          portrait: null, // 上传照片
          attachments: [], // 上传附件
        },
        basicInfo: {}, // 个人信息
        jobIntention: {}, // 求职意向
        educationInfo: [{}], // 教育背景
        experienceInfo: [{}], // 工作经历
        practiceInfo: [{}], // 实习经历
        projectInfo: [{}], // 项目经验
        languageInfo: [{}], // 语言能力
        awardInfo: [{}], // 获奖经历
        selfDescription: {}, // 自我描述
        headhunterRecommendInfo: {
          headhunterRecommendComment: '', // 推荐原因
        }, // 推荐信息
      },
      candidateInfo: null,
      enableSubmit: true, // 预览并提交按钮是否可用，不可用的时候显示为 loading
      jobInfo: {},
      currentApplyFormInfo: {},
      applyFormExtendInfo: null,
    };
    this.fieldsNeedToBeRemoved = new Set();
  }

  initFlag = false;

  componentDidMount() {
    const {
      applicationEntities = {},
      match: {
        params: { orgId, applicationId },
      },
    } = this.props;
    const candidate = applicationEntities[applicationId] || {};
    const jobId = candidate.job?.id || candidate.jobId;
    Promise.all([
      mokaRequest('/api/outer/ats-candidate/candidate/headhunter/queryRecord').post({
        orgId,
        applicationId,
      }),
      mokaRequest('/api/outer/ats-jc/headhunter/job-info').post({ orgId, id: jobId }),
    ]).then(([candidateInfo, jobInfo]) => {
      this.setState({
        candidateInfo,
        jobInfo,
      });
    });
  }

  async componentDidUpdate() {
    const { candidateInfo, jobInfo } = this.state;
    const {
      applyForm,
      match: {
        params: { orgId },
      },
    } = this.props;
    if (!this.initFlag && !isEmpty(candidateInfo) && !isEmpty(jobInfo) && !isEmpty(applyForm)) {
      this.initFlag = true;
      const currentApplyFormInfo = await this.getCurrentApplyFormInfo();
      const newState = { currentApplyFormInfo };
      const { customFields, applyFormId } = currentApplyFormInfo;
      if (candidateInfo?.applicationInfo && customFields) {
        newState.initialValue = candidateToApplyData(
          {
            ...dealCustomDataForApplyForm({ ...candidateInfo.applicationInfo }),
          },
          this.state.initialValue,
          customFields,
          true
        );
      }
      if (applyFormId) {
        getApplyFormExtendInfo({ orgId, ids: [applyFormId] })
          .then(({ applyFormExtendInfoList }) => {
            this.setState({
              ...newState,
              applyFormExtendInfo: applyFormExtendInfoList.find(
                (item) => item.applyFormId === applyFormId
              ),
            });
          })
          .catch(() => this.setState(newState));
      } else {
        this.setState(newState);
      }
    }
  }

  getCurrentApplyFormInfo = async () => {
    const {
      applicationEntities,
      match: {
        params: { applicationId },
      },
      applyForm,
    } = this.props;
    const isHeadhunter =
      applicationEntities[applicationId]?.headhunterType === HEADHUNTER_TYPE.HEADHUNTER;
    const {
      jobInfo: { hireMode },
    } = this.state;
    let currentApplyFormInfo = {};
    if (isHeadhunter) {
      currentApplyFormInfo =
        hireMode === HIRE_MODES.SOCIAL
          ? { ...applyForm.socialApplyForm, applyFormId: applyForm.socialApplyFormId }
          : { ...applyForm.campusApplyForm, applyFormId: applyForm.campusApplyFormId };
    } else {
      currentApplyFormInfo =
        hireMode === HIRE_MODES.SOCIAL
          ? { ...applyForm.rpoSocialApplyForm, applyFormId: applyForm.rpoSocialApplyFormId }
          : { ...applyForm.rpoCampusApplyForm, applyFormId: applyForm.rpoCampusApplyFormId };
    }
    currentApplyFormInfo.applyForm.locales =
      currentApplyFormInfo.applyForm.locales?.map(transformApplyFormLocale);
    await loadLanguagesForApplyInfo(currentApplyFormInfo.applyForm);
    return {
      ...currentApplyFormInfo,
      applyForm: this.getAllApplyForm(currentApplyFormInfo.applyForm),
      customBlocks: [
        ...currentApplyFormInfo.customBlocks,
        {
          id: 'headhunterRecommendInfo',
          title: i18nMark('推荐理由'),
          data: [
            {
              id: 'headhunterRecommendComment',
              name: i18nMark('推荐理由'),
              type: 'text_info',
              placeholder: i18nMark('推荐理由'),
              defaultValue: i18n.t`此处是一段推荐理由`,
            },
          ],
        },
      ],
    };
  };

  getAllApplyForm = (applyForm) => {
    const { setting } = this.props;
    if (
      applyForm &&
      setting.headhunterRecommendComment &&
      setting.headhunterRecommendComment.require
    ) {
      applyForm.setting.headhunterRecommendInfo = {
        show: 1,
        isSelected: ['headhunterRecommendComment'],
        isRequired: setting.headhunterRecommendComment.notNull
          ? ['headhunterRecommendComment']
          : [],
      };
      if (
        applyForm.setting.categoryOrder &&
        !applyForm.setting.categoryOrder.includes('headhunterRecommendInfo')
      ) {
        applyForm.setting.categoryOrder.push('headhunterRecommendInfo');
      }
    }
    return applyForm;
  };

  getContractId = () => {
    const { contractId, applicationId } = this.props.match.params;
    const application = this.props.applicationEntities[applicationId];

    return contractId || application?.contractId;
  };

  onValidateSuccessSubmit = (data) => {
    this.setState({ enableSubmit: false });
    setTimeout(() => {
      this.submit(data);
    });
  };

  submit = (data) => {
    const {
      applicationEntities = {},
      match: {
        params: { orgId, applicationId },
      },
      location: { pathname },
    } = this.props;
    const { candidateInfo, currentApplyFormInfo } = this.state;

    const contractId = this.getContractId();
    const candidate = applicationEntities[applicationId] || {};
    const jobId = candidate.job?.id || candidate.jobId;

    const { applyForm, applyFormId } = currentApplyFormInfo;

    this.setState({ enableSubmit: false });

    const applicationInfo = removeNonDisplayFields(cloneDeep(data), this.fieldsNeedToBeRemoved);

    /**
     * 避免 countryCallingCode 不存在情况
     * 避免 fullPhone 的 callingCode 不存在，仅为手机号情况
     * 避免 fullPhone callingCode 和 countryCallingCode 不一致
     * 避免 countryCallingCode 和 callingCode 不一致
     */
    const fullPhone = applicationInfo?.basicInfo?.fullPhone;
    if (fullPhone) {
      const [callingCode, phone] = parseMobile(fullPhone, true);
      if (phone) {
        if (!callingCode) {
          applicationInfo.basicInfo.fullPhone = `+${CURRENT_COUNTRY_CALLING_CODE} ${phone}`;
        }
        applicationInfo.basicInfo.countryCallingCode = callingCode || CURRENT_COUNTRY_CALLING_CODE;
        applicationInfo.basicInfo.callingCode = applicationInfo.basicInfo.countryCallingCode;
        applicationInfo.basicInfo.phone = phone;
      }
    }

    // 仅存在国家呼叫码没有输入手机号情况下 不传给后端
    const [callingCode, phone] = parseMobile(fullPhone, true);
    if (callingCode && !phone) {
      applicationInfo.basicInfo.fullPhone = '';
      applicationInfo.basicInfo.countryCallingCode = '';
    }

    applicationInfo.uploadInfo = {
      ...handleUploadInfoType(applicationInfo.uploadInfo),
    };

    this.props.uploadCandidateForHeadhunter(
      {
        orgId,
        contractId,
        applicationId,
        jobId,
        applyFormId,
        candidateId: candidateInfo.candidateId,
        ...dealCustomDataForUpdateCandidate(delNotRequireField(applicationInfo, applyForm)),
      },
      (error) => {
        const resultUrl = `${pathname}/result`;
        if (!error || [ERROR_CODES[701027]].includes(error)) {
          this.props.history.push(resultUrl);
        }
        this.setState({
          enableSubmit: true,
        });
      }
    );
  };

  updateFieldsNeedToBeRemoved = (value, action) => {
    if (action === SET_ACTION.ADD) {
      this.fieldsNeedToBeRemoved.add(JSON.stringify(value));
    } else {
      this.fieldsNeedToBeRemoved.delete(JSON.stringify(value));
    }
  };

  render() {
    const {
      orgEntities,
      route,
      match: {
        params: { orgId },
      },
      location,
      applyInfoCallingCodeList,
    } = this.props;

    const { initialValue, enableSubmit, jobInfo, currentApplyFormInfo, applyFormExtendInfo } =
      this.state;

    const currentOrg = orgEntities[orgId] || {};

    const breadcrumbArray = [
      {
        name: i18n.t`首页`,
        url: '/headhunters/',
      },
      {
        name: currentOrg.name,
        url: `/headhunters/${currentOrg.id}/org/jobs`,
      },
      {
        name: i18n.t`推荐历史`,
        url: `/headhunters/${currentOrg.id}/org/applications`,
      },
      {
        name: i18n.t`更新候选人`,
      },
    ];

    const companyName = currentOrg.name;
    // 是否匹配子路由
    const branch = matchRoutes(route.routes, location.pathname);
    const { applyForm, customFields, customBlocks } = currentApplyFormInfo;
    const initValue = handleEndDate(initialValue, applyForm); // initialValue一定不要进行深拷贝，会导致这个页面重新setState的时候操作过的数据失效
    // 将uploadInfo 中的身份证正反面信息添加到basicInfo中 用于回显
    const idCardFront = initValue.uploadInfo.attachments?.find(
      (x) => x.type === APPLICATION_ATTACHMENT_TYPE.ID_CARD_FRONT
    );
    const idCardBack = initValue.uploadInfo.attachments?.find(
      (x) => x.type === APPLICATION_ATTACHMENT_TYPE.ID_CARD_BACK
    );
    initValue.basicInfo.uploadIdPhotoPortraitSide =
      initValue.basicInfo.uploadIdPhotoPortraitSide || idCardFront;
    initValue.basicInfo.uploadIdPhotoNationalEmblemSide =
      initValue.basicInfo.uploadIdPhotoNationalEmblemSide || idCardBack;
    return (
      <div>
        <div className="candidate__header-wrapper">
          <div className="candidate__header">
            <Breadcrumb paths={breadcrumbArray} />
          </div>
        </div>
        {branch.length && route.routes ? (
          renderRoutes(route.routes)
        ) : (
          <div className="panel candidate-info">
            <div className="panel__header candidate-info__header">
              <JobHeader
                job={jobInfo}
                companyName={companyName}
                headhunterJobFields={currentOrg.headhunterJobFields || {}}
              />
            </div>
            <div className="candidate__tab active">
              {!!applyForm && !isEmpty(jobInfo) ? (
                <React.Suspense fallback={<Loading />}>
                  <HeadhunterWeb
                    orgId={orgId}
                    job={jobInfo}
                    applyInfo={applyForm}
                    applyFormExtendInfo={applyFormExtendInfo}
                    initialValue={initValue}
                    customFields={customFields}
                    customBlocks={customBlocks}
                    onValidateSuccessSubmit={this.onValidateSuccessSubmit}
                    submitButtonText={i18nMark('提交')}
                    enableCallingCodeList={applyInfoCallingCodeList}
                    enableSubmit={enableSubmit}
                    updateFieldsNeedToBeRemoved={this.updateFieldsNeedToBeRemoved}
                  />
                </React.Suspense>
              ) : (
                <Loading />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default UpdateCandidateInfo;
